import React, { useEffect, useState } from "react";
import { message, Modal } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { Role_Management, Role_Account_Query } from "../../services/userAPI"; // Ensure correct path
import "./viewRole.css";
import Clan from "../../Images/clan-logo.png";
import Darts from "../../Images/Darts-Logo 1.png";
import Lense from "../../Images/Lense-Logo 1.png";
import { useLocation, useNavigate } from "react-router-dom";
import PageBreadcrumb from '../../reusableField/PageBreadcrumb';

const ViewRole = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const roleId = location.state?.roleId;
    const accountId = location.state?.accountId;
    const accountName = location.state?.accountName;
    const name = location.state?.name;

    // State for product access
    const [productAccess, setProductAccess] = useState({
        DARTS: location.state?.dartsNeeded ?? false,
        CLAN: location.state?.clanNeeded ?? false,
        LENSE: false,
        SYNC: location.state?.syncNeeded ?? false, // Added sync access state
    });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showAccessModal, setShowAccessModal] = useState(false);

    // Fetch job role data using Role_Account_Query
    const { loading: roleLoading, error: roleError, data: roleData, refetch } = useQuery(Role_Account_Query, {
        variables: { accountId: parseInt(accountId) },
        fetchPolicy: "network-only",
        skip: !accountId, // Skip query if accountId is not available
    });

    useEffect(() => {
        if (accountId && roleId) {
            refetch(); // Refetch the query when the component mounts
        }
    }, [accountId, roleId, refetch]);

    // Update productAccess state when roleData is fetched
    useEffect(() => {
        if (roleData?.getAllJobrole) {
            const jobRole = roleData.getAllJobrole.find((role) => role.id === roleId);
            if (jobRole) {
                setProductAccess({
                    DARTS: jobRole.dartsNeeded ?? location.state?.dartsNeeded ?? false,
                    CLAN: jobRole.clanNeeded ?? location.state?.clanNeeded ?? false,
                    LENSE: false,
                    SYNC: jobRole.syncNeeded ?? location.state?.syncNeeded ?? false, // Update sync access state
                });
            }
        }
    }, [roleData, roleId, location.state]);

    const [addJobrole, { loading: addRoleLoading }] = useMutation(Role_Management);

    const handleUpdateAccess = async () => {
        const currentRole = roleData?.getAllJobrole?.find(r => r.id === roleId);
        if (!currentRole) {
            message.error("Role data not available.");
            return;
        }

        // 1. Optimistically update UI
        const newAccess = !productAccess[selectedProduct];
        setProductAccess(prev => ({ ...prev, [selectedProduct]: newAccess }));
        setShowAccessModal(false);

        const variables = {
            accountId: parseInt(accountId),
            jobroleId: roleId,
            name: currentRole.name,

            clanNeeded: selectedProduct === "CLAN" ? newAccess : currentRole.clanNeeded,
            dartsNeeded: selectedProduct === "DARTS" ? newAccess : currentRole.dartsNeeded,
            syncNeeded: selectedProduct === "SYNC" ? newAccess : currentRole.syncNeeded,
            lenseNeeded: selectedProduct === "LENSE" ? newAccess : currentRole.lenseNeeded,

            // all other required fields - use values from currentRole (GET API response)
            dartsFormNeeded: currentRole.dartsFormNeeded,
            pocName: currentRole.pocName || "",
            pocEmail: currentRole.pocEmail || "",
            pocNumber: currentRole.pocContactNumber || "",
            numberLogin: currentRole.numberLogin,
            emailLogin: currentRole.emailLogin,
            mandatoryTimeDistribution: currentRole.mandatoryTimeDistribution,
            signInSignOutRequired: currentRole.signInSignOutRequired,
            locationRequired: currentRole.locationRequired,
            targetEditDays: currentRole.targetEditDays,
            inputEditDays: currentRole.inputEditDays,
            ldmsNeeded: currentRole.ldmsNeeded,
            mirrorNeeded: currentRole.mirrorNeeded,
            geoTracking: currentRole.geoTracking ?? false,
            jobroleAccessUserIds: currentRole.jobroleAccessUsers?.map(u => u.id) || [],
        };

        try {
            await addJobrole({ variables });
            message.success(`${selectedProduct} access updated`);
            await refetch(); // <-- wait for fresh data
        } catch (err) {
            // 2. Revert on error
            setProductAccess(prev => ({ ...prev, [selectedProduct]: !newAccess }));
            message.error("Update failed: " + err.message);
        }
    };

    const products = [
        { name: "LENSE", tagline: "DISCOVER THE UNSEEN", logo: Lense, needed: productAccess.LENSE, route: "/lense-management" },
        { name: "DARTS", tagline: "MAKE EVERYDAY COUNT", logo: Darts, needed: productAccess.DARTS, route: "/productConfiguration" },
        { name: "CLAN", tagline: "WIN AT WORK", logo: Clan, needed: productAccess.CLAN, route: "/clan-management" },
    ];

    const handleProductClick = (product) => {

        if (product.name === "LENSE") {
            return; //  Completely disable LENSE click
        }

        if (productAccess[product.name]) {
            navigate(product.route, {
                state: {
                    roleId,
                    accountId,
                    dartsFormNeeded: roleData?.getAllJobrole.find((role) => role.id === roleId)?.dartsFormNeeded ?? location.state?.dartsFormNeeded,
                    pocName: location.state?.pocName,
                    pocEmail: location.state?.pocEmail,
                    pocNumber: location.state?.pocNumber,
                    numberLogin: location.state?.numberLogin,
                    emailLogin: location.state?.emailLogin,
                    mandatoryTimeDistribution: location.state?.mandatoryTimeDistribution,
                    targetEditDays: location.state?.targetEditDays,
                    inputEditDays: location.state?.inputEditDays,
                    name,
                    dartsNeeded: productAccess.DARTS,
                    clanNeeded: productAccess.CLAN,
                    syncNeeded: productAccess.SYNC,
                    accountName,
                    geoTracking: location.state?.geoTracking,
                    jobroleAccessUserIds: location.state?.jobroleAccessUserIds,
                    signInSignOutRequired: location.state?.signInSignOutRequired,
                    locationRequired: location.state?.locationRequired,
                    ldmsNeeded: location.state?.ldmsNeeded,
                    mirrorNeeded: location.state?.mirrorNeeded,
                },
            });
        } else {
            setSelectedProduct(product.name);
            setShowAccessModal(true);
        }
    };

    const handleDiscardClick = () => {
        navigate("/admin");
    };

    return (
        <div className="user-profile-container">
            <div className="product-selection-container">
                <PageBreadcrumb homePath="/admin" onBack={() => navigate("/admin")}>
                    <span onClick={() => navigate("/admin")} className="bold-text" style={{ cursor: 'pointer' }}>
                        {accountName}
                    </span>
                    <span>&gt;</span>
                    <span style={{ fontWeight: 'bold' }}>{name}</span>
                </PageBreadcrumb>

                {roleLoading && <div>Loading...</div>}
                {roleError && <div>Error: {roleError.message}</div>}

                <h2 className="title">Select The Product To Configure</h2>
                <div className="product-grid">
                    {products.map((product, index) => (
                        <div
                            key={index}
                            className={`product-card ${productAccess[product.name] ? "active" : "disabled"} ${selectedProduct === product.name ? "selected" : ""
                                }`}
                            onClick={product.name === "LENSE" ? undefined : () => handleProductClick(product)}
                            style={{
                                cursor: product.name === "LENSE" ? "not-allowed" : "pointer",
                                backgroundColor: product.name === "LENSE" ? "#E5E7EB" : undefined
                            }}

                        >
                            <img src={product.logo} alt={product.name} className="product-logo" />
                        </div>
                    ))}
                </div>
                <br />

                <div className="user-management-and-role-management-header">
                    <button
                        className="userManagementRedirect"
                        onClick={() =>
                            navigate("/user-management", {
                                state: { accountId, roleId, accountName, name },
                            })
                        }
                    >
                        User Management
                    </button>
                    <button
                        className="roleManagementRedirect"
                        onClick={() =>
                            navigate("/role-management", {
                                state: { accountId, roleId, accountName, name },
                            })
                        }
                    >
                        Role Management
                    </button>
                </div>
                <div className="user-management-and-role-management-header">
                    <button
                        className="faqRedirect"
                        onClick={() => navigate("/faqs", {
                            state: { accountId, roleId, accountName, name }
                        })}
                    >
                        FAQ's
                    </button>
                    <button
                        className="faqRedirect"
                        onClick={() => navigate("/mark-holidays", {
                            state: { accountId, roleId, accountName, name }
                        })}
                    >
                        Mark Holidays
                    </button>
                </div>

                <Modal
                    title="Access Required"
                    open={showAccessModal}
                    onCancel={() => setShowAccessModal(false)}
                    footer={[
                        <button
                            key="no"
                            onClick={() => setShowAccessModal(false)}
                            className="cancel-button"
                            style={{ marginRight: "10px" }}
                        >
                            No
                        </button>,
                        <button
                            key="yes"
                            type="primary"
                            onClick={handleUpdateAccess}
                            disabled={addRoleLoading}
                            className="button bg-[#AD0101] text-white px-10 py-2 rounded-lg"
                        >
                            {addRoleLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Yes"}
                        </button>,
                    ]}
                >
                    <p>Do you need to give access to {selectedProduct}?</p>
                </Modal>
            </div>
        </div>
    );
};

export default ViewRole;